import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Cookies from 'universal-cookie';
import { Form, FormLabel } from '@edx/paragon';
import { getConfig } from '@edx/frontend-platform';
import { AppContext } from '@edx/frontend-platform/react';
import { injectIntl, intlShape } from '@edx/frontend-platform/i18n';

import RedirectModal from '../redirect-modal';
import { DEFAULT_COUNTRY_CODE } from '../constants';
import messages from './messages';
import { buildSupportedCountriesData, customizeCountryLabels, getCookieDomain } from './utils';

const LocationSelector = ({
  intl, displayTitle, displayControls, isConfirmChanges, id,
}) => {
  const cookies = new Cookies();
  const cookieKey = getConfig().USER_COUNTRY_CODE_COOKIE_NAME || 'user_country_code';
  const defaultLocation = cookies.get(cookieKey) || DEFAULT_COUNTRY_CODE;
  const [userLocation, setUserLocation] = useState(defaultLocation);
  const [isOpen, setOpen] = useState(false);
  const { authenticatedUser } = useContext(AppContext);

  const countries = useMemo(
    () => customizeCountryLabels(buildSupportedCountriesData()).sort((a, b) => a.label.localeCompare(b.label)),
    [],
  );

  const handleFormReset = () => {
    setOpen(false);
    setUserLocation(defaultLocation);
  };

  const handleFormSubmit = () => {
    cookies.set(cookieKey, userLocation, {
      path: '/', domain: getCookieDomain(), secure: true, sameSite: 'lax',
    });
    window.location.reload();
  };

  const handleLocationChange = ({ target }) => {
    setUserLocation(target.value);

    if (isConfirmChanges) {
      setOpen(true);
    }
  };

  return (
    <>
      <form className="location-selector-form">
        {displayTitle && (
          <h3 className="welcome-modal-title">
            {intl.formatMessage(messages.title)}
          </h3>
        )}
        <Form.Group className="form-group-select mb-0" size="sm">
          <FormLabel>{intl.formatMessage(messages.locationLabel)}:</FormLabel>
          <Form.Control
            id={id}
            name={id}
            as="select"
            disabled={authenticatedUser}
            value={userLocation}
            onChange={handleLocationChange}
          >
            {countries.map(({ value, label }) => <option key={value} value={value}>{label}</option>)}
          </Form.Control>
        </Form.Group>
        {displayControls && (
          <button
            type="button"
            id="welcome-modal-submit-btn"
            onClick={handleFormSubmit}
          >
            {intl.formatMessage(messages.submitBtn)}
          </button>
        )}
      </form>
      <RedirectModal
        isOpen={isOpen}
        close={handleFormReset}
        submit={handleFormSubmit}
      />
    </>
  );
};

LocationSelector.defaultProps = {
  id: 'location',
  displayTitle: false,
  displayControls: false,
  isConfirmChanges: false,
};

LocationSelector.propTypes = {
  intl: intlShape.isRequired,
  id: PropTypes.string,
  displayTitle: PropTypes.bool,
  displayControls: PropTypes.bool,
  isConfirmChanges: PropTypes.bool,
};

export default injectIntl(LocationSelector);
